import React, { memo } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import type {
	InlineCommentCreateComponentProps,
	InlineCommentViewComponentProps,
} from '@atlaskit/editor-plugins/annotation';

import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor';
import { LoadableAfterPaint } from '@confluence/loadable';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter';
import { getEditorResolvedState } from '@confluence/comments-util/entry-points/stateResolver';

import { CreateManager } from './CreateManager';
import type { CreateComponentProps } from './CreateComponent';
import { ViewManager } from './ViewManager';
import type { ViewComponentProps } from './ViewComponent';

const CreateComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CreateComponent" */ './CreateComponent'))
			.CreateComponent,
});

const ViewComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ViewComponent" */ './ViewComponent'))
			.ViewComponent,
});

const ViewComponentMemoized = ({
	pageId,
	editorActions,
}: {
	pageId: string;
	editorActions?: EditorActions;
}) =>
	memo((props: InlineCommentViewComponentProps) => {
		return <ViewComponent pageId={pageId} editorActions={editorActions} {...props} />;
	});

const CreateComponentMemoized = ({ pageId }: { pageId: string }) =>
	memo((props: InlineCommentCreateComponentProps) => {
		return <CreateComponent pageId={pageId} {...props} />;
	});

type GetEditorInlineCommentOptionsType = {
	pageId: string;
	editorActions: EditorActions;
	triggerAnalyticsEvent: any;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	setEditorCreateProps: (
		createProps: (InlineCommentCreateComponentProps & CreateComponentProps) | null,
	) => void;
	setEditorViewProps: (
		viewProps: (InlineCommentViewComponentProps & ViewComponentProps) | null,
	) => void;
	editorAnnotationProviderManagersEnabled: boolean;
	getCanAddComments: () => boolean;
	contentType?: string;
};

export const getEditorInlineCommentOptions = ({
	pageId,
	editorActions,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	editorAnnotationProviderManagersEnabled,
	getCanAddComments,
	contentType,
}: GetEditorInlineCommentOptionsType) => {
	let updateSubscriber = getEditorAnnotationEventEmitter();
	if (!updateSubscriber?.off) {
		updateSubscriber = null;
	}

	return {
		viewComponent: editorAnnotationProviderManagersEnabled
			? ViewManager({
					pageId,
					editorActions,
				})
			: ViewComponentMemoized({ pageId, editorActions }),
		createComponent: editorAnnotationProviderManagersEnabled
			? CreateManager({ pageId })
			: CreateComponentMemoized({ pageId }),
		getState: (annotations: any) =>
			getEditorResolvedState({
				annotations,
				pageId,
				triggerAnalyticsEvent,
				updateUnresolvedInlineComment,
			}),
		updateSubscriber,
		disallowOnWhitespace: true,
		isToolbarAbove: true,
		supportedBlockNodes: ['media'],
		onCommentButtonMount: () => CommentEditor.preload(),
		getCanAddComments,
		contentType,
	};
};
